import { useReadQuery } from '@apollo/client';
import { type ReactNode, Suspense, useState } from 'react';
import { ClientLoaderFunction, useParams } from 'react-router';
import { ConnectionStatusType } from '@/components/connection-status/connection-status-type.ts';
import { ErrorBanner } from '@/components/error-page/error-banner.tsx';
import { HighlightedFeedWrapper } from '@/components/highlighted-feed-wrapper/highlighted-feed-wrapper.tsx';
import { useRedesign } from '@/feature-flags/redesign/use-redesign.ts';
import { useMe } from '@/routes/_app/use-me.ts';
import {
  AuthenticatedStreamLoaderData,
  useStreamLoaderData,
} from '../_app.p.streams_.$id/loader.ts';
import { SeoHandle } from '../sitemap[.]xml/generate-sitemap.ts';
import { AuthenticatedStreamPreviewAlert } from './authenticated-stream-preview-alert.tsx';
import { DelayedPreviewAlert } from './delayed-preview-alert.tsx';
import { streamPostsLoader } from './loader.ts';
import { streamPostsWrapper } from './page.css.ts';
import { streamSitemap } from './sitemap.ts';
import {
  streamDataCardV2,
  streamDataTitleV2,
  streamDataToolbarV2,
  streamEventFallbackBarV2,
  streamEventListV2,
  streamEventRowV2,
} from './stream-data-v2.css.ts';
import { StreamPostFeedSkeleton } from './stream-post-feed-skeleton.tsx';
import { StreamPostsAction } from './stream-posts-action.tsx';
import { StreamPostsFeed } from './stream-posts-feed.tsx';
import { streamPostsShowLockCta } from './stream-posts-lock.ts';
import { StreamPreviewFeed } from './stream-preview-feed.tsx';

export const handle: SeoHandle = {
  sitemap: streamSitemap,
};

export const clientLoader = streamPostsLoader;
(clientLoader as ClientLoaderFunction).hydrate = true;

export const ErrorBoundary = ErrorBanner;

const StreamEventFeedFallback = () => (
  <div className={streamEventListV2} aria-busy="true">
    {Array.from({ length: 3 }, (_, index) => (
      <div key={index}>
        <div className={streamEventRowV2}>
          <div className={streamEventFallbackBarV2} />
        </div>
      </div>
    ))}
  </div>
);

const StreamDataCard = ({
  toolbar,
  notice,
  feed,
}: {
  toolbar?: ReactNode;
  notice?: ReactNode;
  feed: ReactNode;
}) => (
  <div className={streamDataCardV2}>
    <div className={streamDataToolbarV2}>
      <h2 className={streamDataTitleV2}>Data stream</h2>
      {toolbar}
    </div>
    {notice}
    <Suspense fallback={<StreamEventFeedFallback />}>{feed}</Suspense>
  </div>
);

const AuthenticatedStreamData = ({
  subscriptionStatus,
  notice,
  feed,
}: {
  subscriptionStatus: ConnectionStatusType;
  notice?: ReactNode;
  feed: ReactNode;
}) => {
  const { streamQueryRef } =
    useStreamLoaderData() as AuthenticatedStreamLoaderData;
  const { data } = useReadQuery(streamQueryRef);
  const me = useMe();
  const stream = data?.stream;
  const showLock =
    !!stream &&
    streamPostsShowLockCta(stream, me.username === stream.author.username);

  return (
    <StreamDataCard
      toolbar={
        showLock ? undefined : (
          <Suspense fallback={null}>
            <StreamPostsAction subscriptionStatus={subscriptionStatus} />
          </Suspense>
        )
      }
      notice={showLock ? undefined : notice}
      feed={feed}
    />
  );
};

export default function StreamFeed() {
  const [subscriptionStatus, setSubscriptionStatus] = useState(
    ConnectionStatusType.Connected,
  );

  const { authenticated } = useStreamLoaderData();
  const streamId = useParams().id as string;
  const redesign = useRedesign();

  const skeletonItemCount = 5;
  const feed = authenticated ? (
    <StreamPostsFeed
      setSubscriptionStatus={setSubscriptionStatus}
      skeletonItemCount={skeletonItemCount}
    />
  ) : (
    <StreamPreviewFeed
      streamId={streamId}
      skeletonItemCount={skeletonItemCount}
    />
  );

  const previewAlert = authenticated ? (
    <Suspense fallback={null}>
      <AuthenticatedStreamPreviewAlert />
    </Suspense>
  ) : (
    <DelayedPreviewAlert>
      This is a delayed preview. Log in to follow the stream and see new posts
      in real time.
    </DelayedPreviewAlert>
  );

  if (redesign) {
    return authenticated ? (
      <Suspense fallback={null}>
        <AuthenticatedStreamData
          subscriptionStatus={subscriptionStatus}
          notice={previewAlert}
          feed={feed}
        />
      </Suspense>
    ) : (
      <StreamDataCard notice={previewAlert} feed={feed} />
    );
  }

  return (
    <>
      {previewAlert}

      <HighlightedFeedWrapper
        label="Data stream"
        className={streamPostsWrapper}
        action={
          authenticated && (
            <Suspense fallback={null}>
              <StreamPostsAction subscriptionStatus={subscriptionStatus} />
            </Suspense>
          )
        }
      >
        <Suspense
          fallback={<StreamPostFeedSkeleton count={skeletonItemCount} />}
        >
          {feed}
        </Suspense>
      </HighlightedFeedWrapper>
    </>
  );
}
